import { useRef, useState } from 'react';
import { FileUp, Loader2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { analyzeReferenceFile, isSupportedReferenceFile } from '@/lib/attachments';
import type { AttachmentPurpose, GenerateAttachment } from '@/lib/generate';

const MAX_ATTACHMENTS = 5;
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const MAX_DOC_BYTES = 10 * 1024 * 1024;

export type AttachmentCard =
  | { status: 'analyzing'; localId: string; name: string }
  | { status: 'error'; localId: string; name: string; error: string }
  | ({ status: 'ready' } & GenerateAttachment);

export interface ReferenceMaterialsUploaderProps {
  attachments: AttachmentCard[];
  onChange: (attachments: AttachmentCard[]) => void;
  disabled?: boolean;
}

function defaultPurpose(): AttachmentPurpose {
  return 'reference';
}

export function ReferenceMaterialsUploader({
  attachments,
  onChange,
  disabled = false,
}: ReferenceMaterialsUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const readyCount = attachments.filter((a) => a.status === 'ready').length;
  const analyzing = attachments.some((a) => a.status === 'analyzing');

  async function handleFiles(files: FileList | null) {
    if (!files?.length || disabled) return;
    let next = [...attachments];

    for (const file of Array.from(files)) {
      if (next.filter((a) => a.status === 'ready' || a.status === 'analyzing').length >= MAX_ATTACHMENTS) {
        break;
      }
      if (!isSupportedReferenceFile(file)) continue;

      const isImage = file.type.startsWith('image/');
      const max = isImage ? MAX_IMAGE_BYTES : MAX_DOC_BYTES;
      const localId = crypto.randomUUID();

      if (file.size > max) {
        next = [
          ...next,
          {
            status: 'error',
            localId,
            name: file.name,
            error: isImage ? 'Image max 5 MiB' : 'Document max 10 MiB',
          },
        ];
        onChange(next);
        continue;
      }

      next = [...next, { status: 'analyzing', localId, name: file.name }];
      onChange(next);

      try {
        const result = await analyzeReferenceFile(file, defaultPurpose());
        next = next.map((a) =>
          a.status === 'analyzing' && a.localId === localId
            ? { status: 'ready' as const, ...result }
            : a,
        );
        onChange(next);
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Analysis failed';
        next = next.map((a) =>
          a.status === 'analyzing' && a.localId === localId
            ? { status: 'error', localId, name: file.name, error: msg }
            : a,
        );
        onChange(next);
      }
    }
  }

  function updatePurpose(id: string, purpose: AttachmentPurpose) {
    onChange(
      attachments.map((a) =>
        a.status === 'ready' && a.id === id ? { ...a, purpose } : a,
      ),
    );
  }

  function removeAt(index: number) {
    onChange(attachments.filter((_, i) => i !== index));
  }

  return (
    <div className="space-y-2">
      <Label className="text-muted-foreground text-xs uppercase tracking-wide">
        Reference materials
      </Label>
      <div
        role="button"
        tabIndex={0}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          void handleFiles(e.dataTransfer.files);
        }}
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        className={cn(
          'border-blue-200 bg-blue-50/50 hover:bg-blue-100/50 hover:border-blue-300 flex cursor-pointer flex-col items-center justify-center rounded-md border border-dashed px-4 py-6 text-center transition-colors dark:border-blue-900 dark:bg-blue-900/20 dark:hover:bg-blue-900/40',
          dragOver && 'border-blue-400 bg-blue-100/80 dark:border-blue-700 dark:bg-blue-900/60',
          disabled && 'pointer-events-none opacity-60',
        )}
      >
        <FileUp className="text-blue-500 mb-2 size-5" />
        <p className="text-foreground text-xs font-medium">Drop images or documents</p>
        <p className="text-muted-foreground mt-1 text-[10px]">
          PNG, JPG, WebP, PDF, TXT, Markdown, CSV · max {MAX_ATTACHMENTS} files
        </p>
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          multiple
          accept=".png,.jpg,.jpeg,.webp,.pdf,.txt,.md,.markdown,.csv,text/*,application/pdf,image/*"
          disabled={disabled}
          onChange={(e) => {
            void handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      {attachments.length > 0 ? (
        <ul className="space-y-2">
          {attachments.map((a, i) => (
            <li
              key={a.status === 'ready' ? a.id : a.localId}
              className="border-border bg-card rounded-md border p-2.5"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-xs font-medium">{a.name}</div>
                  {a.status === 'analyzing' ? (
                    <p className="text-muted-foreground mt-1 flex items-center gap-1 text-[10px]">
                      <Loader2 className="size-3 animate-spin" />
                      Analyzing…
                    </p>
                  ) : null}
                  {a.status === 'error' ? (
                    <p className="text-destructive mt-1 text-[10px]">{a.error}</p>
                  ) : null}
                  {a.status === 'ready' ? (
                    <>
                      <p className="text-muted-foreground mt-1 line-clamp-3 text-[10px] leading-snug">
                        {a.summary}
                      </p>
                      <div className="mt-2">
                        <Select
                          value={a.purpose}
                          onValueChange={(v) => updatePurpose(a.id, v as AttachmentPurpose)}
                          disabled={disabled}
                        >
                          <SelectTrigger className="h-7 text-[10px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="summarize">Summarize</SelectItem>
                            <SelectItem value="reference">Reference</SelectItem>
                            <SelectItem value="extract_offers">Extract offers</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  ) : null}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-7 shrink-0"
                  disabled={disabled || a.status === 'analyzing'}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeAt(i);
                  }}
                >
                  <Trash2 className="size-3.5" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      {analyzing ? (
        <p className="text-muted-foreground text-[10px]">Analyzing materials…</p>
      ) : null}
      {readyCount > 0 && !analyzing ? (
        <p className="text-muted-foreground text-[10px]">
          Reference materials will be summarized before generation.
        </p>
      ) : null}
    </div>
  );
}

export function readyAttachments(cards: AttachmentCard[]): GenerateAttachment[] {
  return cards.filter((a): a is AttachmentCard & { status: 'ready' } => a.status === 'ready');
}

export function isAnalyzingAttachments(cards: AttachmentCard[]): boolean {
  return cards.some((a) => a.status === 'analyzing');
}
